import React, { SyntheticEvent, useMemo, useState } from "react";
import classnames from "classnames";
import {
  AutoTooltipLabel,
  Button,
  IconButton,
  Popover,
  Skeleton,
  stylize,
} from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";
import EnvLogoWhite from "images/EnvLogoWhite";
import {
  faCircleHalfStroke,
  faShield,
  faSignOut,
} from "@fortawesome/pro-regular-svg-icons";
import { UserInfo } from "types/UserInfo";
import { useThemeMode } from "scenes/App/themeModeContext";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  loadingDashboard: boolean;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface DashboardHeaderProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
  user?: UserInfo;
  isDirectLoginEnabled: boolean;

  goToSecuritySettings: () => void;
  signout: () => void;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & DashboardHeaderProps;

const DashboardHeader = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    user,
    isDirectLoginEnabled,
    goToSecuritySettings,
    signout,
    loadingDashboard,
  } = props;
  const { toggleThemeMode } = useThemeMode();

  const [showPopover, setShowPopover] = useState<boolean>(false);

  const popoverContent = useMemo(
    () => (
      <>
        <div className={classes.popoverContainer}>
          <div className={classes.popoverItem}>
            <label htmlFor="username" className={classes.popoverItemLabel}>
              {formatMessage(messages.username)}
            </label>
            <span id="username" className={classes.popoverItemValue}>
              <AutoTooltipLabel>
                {user?.preferred_username || ""}
              </AutoTooltipLabel>
            </span>
          </div>
          <div className={classes.popoverItem} style={{ marginTop: "12px" }}>
            <label htmlFor="email" className={classes.popoverItemLabel}>
              {formatMessage(messages.email)}
            </label>
            <span id="email" className={classes.popoverItemValue}>
              <AutoTooltipLabel>{user?.email || ""}</AutoTooltipLabel>
            </span>
          </div>
        </div>
      </>
    ),
    [user]
  );

  const getInitials = (currentUser?: UserInfo) => {
    const firstName = currentUser?.given_name?.trim();
    const lastName = currentUser?.family_name?.trim();

    if (!lastName) {
      return "";
    }

    if (!firstName) {
      return lastName.charAt(0);
    }

    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classes.logo}>
        <EnvLogoWhite />
      </div>
      <div className={classes.buttonContainer}>
        <div className={classes.userContainer}>
          <Popover
            open={showPopover}
            mode="light"
            controlled
            triggers={["click", "focus"]}
            placement="bottom-end"
            content={popoverContent}
            disablePortal
            onClose={() => setShowPopover(false)}
            className={classes.popoverClass}
          >
            <div
              className={classes.userAvatar}
              role="button"
              id="avatar-button"
              aria-label="avatar-button"
              onClick={(e: SyntheticEvent) => {
                setShowPopover(true);
                e.stopPropagation();
              }}
              tabIndex={0}
              onKeyDown={() => setShowPopover(true)}
              onFocus={() => setShowPopover(true)}
              onBlur={() => setShowPopover(false)}
            >
              {getInitials(user)}
            </div>
          </Popover>
        </div>
        <IconButton
          icon={faCircleHalfStroke}
          onClick={toggleThemeMode}
          className={classes.themeButton}
          aria-label="change color mode"
        />
        {isDirectLoginEnabled && (
          <>
            {loadingDashboard ? (
              <div className={classes.skeleton}>
                <Skeleton height={17} width={100} />
              </div>
            ) : (
              <Button
                className={classes.headerBtn}
                type="button"
                onClick={() => {
                  goToSecuritySettings();
                }}
                iconLeft={faShield}
                buttonStyle="link"
                name={formatMessage(messages.securitySettings)}
              />
            )}
            {loadingDashboard ? (
              <div className={classes.skeleton}>
                <Skeleton height={17} width={100} />
              </div>
            ) : (
              <Button
                onClick={() => {
                  signout();
                }}
                type="button"
                iconLeft={faSignOut}
                name={formatMessage(messages.signout)}
                buttonStyle="link"
                className={classes.headerBtn}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

DashboardHeader.defaultProps = defaultProps;

export default withIntl(stylize<Props, DefaultProps>(styles)(DashboardHeader));
